import { graphql, PageProps } from 'gatsby';

import { setARIATranslations } from '@mewa/ui';

import { useConversionButtonLabel } from '../../a-b-testing/cta-label-experiment';
import { ContactTeaserWrapper } from '../../components/contact-teaser/contact-teaser-wrapper';
import { NavigationData } from '../../components/header/types';
import { possibleComponents } from '../../components/strapi/content-section';
import { getFirstAssetUrl } from '../../helpers/first-asset-url';
import { LayoutType } from '../../layouts/types';
import { GlobalPageContext } from '../../types';
import { setShowMoreLessTranslations } from '../dynamic-content-page/content-components/person-tile';
import {
  isSapForm,
  SapFormData,
} from '../dynamic-content-page/content-components/sap-form';
import { ContentSection } from '../dynamic-content-page/content-section';
import {
  ContentSectionsTranslations,
  PossibleContentSections,
} from '../dynamic-content-page/fragment-and-types';
import {
  PossibleHeaderComponents,
  StrapiHeaderSection,
} from './header/header-section';
import {
  PossibleStageComponents,
  StrapiStageSection,
} from './stage/stage-section';

export { Head } from '../../layouts/head';

export const query = graphql`
  query ($id: String!, $locale: String!, $lang: String!) {
    ...NavigationFragment

    strapiCampaignPage(id: { eq: $id }) {
      layoutType
      leadInterest
      showContactTeaser
      listInSearchResults

      slug: Slug

      ...ContentSectionFragment
      ...CampaignHeaderSectionFragment
      ...CampaignStageSectionFragment
    }

    translations: translation(locale: { eq: $locale }) {
      ...ContentSectionTranslations
      general_form_button_label_back
      general_form_button_label_next
      campaign_module_person_wall_load_all
      campaign_module_person_wall_show_less
    }
  }
`;

type CampaignPageProps = NavigationData & {
  strapiCampaignPage: {
    listInSearchResults?: boolean;
    layoutType: LayoutType;
    showContactTeaser: boolean;
    slug: string;
    content: PossibleContentSections[] | null;
    header: PossibleHeaderComponents[] | null;
    stage: PossibleStageComponents[] | null;
  };
  translations: {
    general_form_button_label_back: string;
    general_form_button_label_next: string;
    campaign_module_person_wall_load_all: string;
    campaign_module_person_wall_show_less: string;
  } & ContentSectionsTranslations;
};

type PageContext = GlobalPageContext & {
  piwikEventNameForHeaderCTAButton?: string;
};

const CampaignPage = ({
  data,
  pageContext,
  location,
}: PageProps<CampaignPageProps, PageContext>) => {
  return (
    <CampaignContainer
      data={data}
      pageContext={pageContext}
      locationKey={location.key || ''}
      locationSearch={location.search || ''}
    />
  );
};

export const CampaignContainer = ({
  data,
  pageContext: {
    lang,
    locale,
    langPrefix,
    piwikEventNameForHeaderCTAButton,
    market,
  },
  locationKey,
  locationSearch,
}: {
  data: CampaignPageProps;
  pageContext: PageContext;
  locationKey: string;
  locationSearch: string;
}) => {
  const sapForm = data.strapiCampaignPage.content?.find<SapFormData>(
    (component): component is SapFormData => isSapForm(component),
  );

  setARIATranslations({
    label_back: data.translations.general_form_button_label_back,
    label_next: data.translations.general_form_button_label_next,
  });

  setShowMoreLessTranslations(
    data.translations.campaign_module_person_wall_load_all,
    data.translations.campaign_module_person_wall_show_less,
  );

  const { ctaLabel } = useConversionButtonLabel({
    translations: data.headerAndFooterTranslations,
  });

  return (
    <div
      data-pagefind-ignore={
        !data.strapiCampaignPage.listInSearchResults ? 'all' : undefined
      }
    >
      <div
        style={{ visibility: 'hidden' }}
        data-pagefind-meta={`image:${getFirstAssetUrl(data.strapiCampaignPage.content)}`}
      />
      <StrapiHeaderSection
        lang={lang}
        langPrefix={langPrefix}
        market={market}
        locale={locale}
        locationKey={locationKey}
        navigationData={data}
        locationSearch={locationSearch}
        showCTA={sapForm === undefined}
        header={data.strapiCampaignPage.header || []}
      />
      <StrapiStageSection
        langPrefix={langPrefix}
        navigationData={data}
        stage={data.strapiCampaignPage.stage || []}
        sapForm={sapForm}
      />

      <ContentSection
        possibleComponents={possibleComponents}
        lang={lang}
        locale={locale}
        langPrefix={langPrefix}
        conversion={{
          label: ctaLabel,
          link: `${langPrefix}/${data.navigationLinks.conversion.contact.slug}`,
        }}
        sections={data.strapiCampaignPage.content ?? []}
        piwikEventNameForHeaderCTAButton={piwikEventNameForHeaderCTAButton}
        sapForm={sapForm}
        navigationLinks={data.navigationLinks}
        translations={data.translations}
      />
      {data.strapiCampaignPage.showContactTeaser && (
        <ContactTeaserWrapper
          contactTeaserLabels={data.contactTeaserLabels}
          navigationLinks={data.navigationLinks}
          langPrefix={langPrefix}
        />
      )}
    </div>
  );
};

CampaignPage.noContactTeaser = true;
CampaignPage.layout = LayoutType.Campaign;

export default CampaignPage;
